import {
  Button,
  Center,
  Flex,
  Group,
  Select,
  SimpleGrid,
  Stack,
  Text,
} from "@mantine/core";
import { DatePicker, type DatePickerProps } from "@mantine/dates";
import * as df from "date-fns";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useSearchParams } from "wouter";
import { client } from "@/api-client";
import type {
  AvailabilityOutput,
  Day,
  TrainerDetailsSessionTypeOutput,
} from "@/api-types";
import { FancyLoader } from "@/components/FancyLoader";
import { Header } from "@/components/Header";
import { LinkButton } from "@/components/LinkButton";
import { UniversalRoutes, useNavigator } from "@/routers/routes";

// Helper function that excludes border dates from being considered within interval
const isStrictlyWithinInterval = (
  date: Date,
  interval: { start: Date; end: Date },
): boolean => {
  return df.isAfter(date, interval.start) && df.isBefore(date, interval.end);
};

const RequestSessionContent = (props: {
  trainerId: string;
  sessionTypes: [
    TrainerDetailsSessionTypeOutput,
    ...TrainerDetailsSessionTypeOutput[],
  ];
  availability: AvailabilityOutput;
  selectedSessionTypeId: string;
  onSessionTypeChange: (sessionTypeId: string) => void;
}) => {
  const { navigate } = useNavigator();
  const [selectedDate, setSelectedDate] = useState<Date | null>(null);
  const [selectedSlots, setSelectedSlots] = useState<Date[]>([]);
  const [selectedSessionType, setSelectedSessionType] = useState(
    props.sessionTypes.find(
      (type) => type.id === props.selectedSessionTypeId,
    ) || props.sessionTypes[0],
  );

  const {
    trainerId,
    sessionTypes,
    availability,
    selectedSessionTypeId,
    onSessionTypeChange,
  } = props;

  // Update selected session type when the ID changes (from parent)
  useEffect(() => {
    const sessionType = sessionTypes.find(
      (type) => type.id === selectedSessionTypeId,
    );
    if (sessionType && sessionType.id !== selectedSessionType.id) {
      setSelectedSessionType(sessionType);
      setSelectedSlots([]); // Clear selected slots when session type changes
    }
  }, [selectedSessionTypeId, sessionTypes, selectedSessionType.id]);

  const instantBooking = availability.instantBooking;

  const requestSessionMutation = client.useMutation(
    "post",
    "/owner/sessions/request",
  );

  const excludeDate = useCallback(
    (dateStr: string) => {
      const date = new Date(dateStr);
      const dayIndex = date.getDay();

      const day = ["SUN", "MON", "TUE", "WED", "THU", "FRI", "SAT"][
        dayIndex
      ] as Day;
      return availability.dayTimeRanges[day] === undefined;
    },
    [availability.dayTimeRanges],
  );

  const renderDate: DatePickerProps["renderDay"] = useCallback(
    (dateStr: string) => {
      const date = new Date(dateStr);
      const day = date.getDate();
      const hasSelectedSlots = selectedSlots.some((slot) =>
        df.isSameDay(slot, date),
      );
      if (hasSelectedSlots) {
        return (
          <Flex
            w="100%"
            h="100%"
            justify="center"
            align="center"
            bd="1px solid blue"
            style={{ borderRadius: "5px" }}
          >
            <div>{day}</div>
          </Flex>
        );
      }
      return <div>{day}</div>;
    },
    [selectedSlots],
  );

  const blockedSlots = useMemo(
    () =>
      availability.blockedSlots.map((slot) => ({
        start: df.parseISO(slot.startTime),
        end: df.parseISO(slot.endTime),
      })),
    [availability.blockedSlots],
  );

  const calculateAvailableTimes = useCallback(
    (date: Date) => {
      const dayIndex = df.getDay(date);
      const day = ["SUN", "MON", "TUE", "WED", "THU", "FRI", "SAT"][
        dayIndex
      ] as Day;
      const dayTimeRange = availability.dayTimeRanges[day];

      if (!dayTimeRange) {
        return [];
      }

      const startTime = dayTimeRange?.startTime;
      const endTime = dayTimeRange?.endTime;
      // TODO: timezones
      const startWorkTime = df.set(date, {
        hours: Math.floor(startTime / 100),
        minutes: startTime % 100,
      });
      const endWorkTime = df.set(date, {
        hours: Math.floor(endTime / 100),
        minutes: endTime % 100,
      });

      const times = [];
      let start = startWorkTime;
      let end = df.addMinutes(start, selectedSessionType.durationMinutes);
      while (!df.isAfter(end, endWorkTime)) {
        debugger;
        const inBlock = blockedSlots.some(
          (slot) =>
            isStrictlyWithinInterval(start, slot) ||
            isStrictlyWithinInterval(end, slot),
        );

        if (!inBlock) {
          times.push(start);
        }

        start = df.addMinutes(start, availability.stepMinutes);
        end = df.addMinutes(start, selectedSessionType.durationMinutes);
      }
      return times;
    },
    [availability, blockedSlots, selectedSessionType.durationMinutes],
  );

  const availableTimes = useMemo(
    () => (!selectedDate ? [] : calculateAvailableTimes(selectedDate)),
    [selectedDate, calculateAvailableTimes],
  );

  const onSlotClick = (slot: Date) => {
    if (instantBooking) {
      setSelectedSlots([slot]);
    } else {
      const newSlots = selectedSlots.some((t) => df.isEqual(t, slot))
        ? selectedSlots.filter((t) => !df.isEqual(t, slot))
        : [...selectedSlots, slot];
      setSelectedSlots(newSlots);
    }
  };

  const onRemoveSlot = (slot: Date) => {
    setSelectedSlots(selectedSlots.filter((t) => t !== slot));
  };

  const handleSubmit = async () => {
    if (!selectedSessionType) return;

    const session = await requestSessionMutation.mutateAsync({
      body: {
        trainerId,
        requestedTimes: selectedSlots.map((slot) => df.formatISO(slot)),
        sessionTypeId: selectedSessionType.id,
      },
    });
    navigate({
      path: UniversalRoutes.SESSIONS.DETAIL,
      params: { sessionId: session.sessionId },
    });
  };

  const firstAvailableDate = useMemo(() => {
    const minDate = df.parseISO(availability.minDate);
    const maxDate = df.parseISO(availability.maxDate);

    // Min date mau not be the first working day, so we need to find the first working day
    let currentDate = minDate;

    while (
      df.isBefore(currentDate, maxDate) ||
      df.isEqual(currentDate, maxDate)
    ) {
      const currentDateStr = df.format(currentDate, "yyyy-MM-dd");
      if (!excludeDate(currentDateStr)) {
        return currentDateStr;
      }

      currentDate = df.addDays(currentDate, 1);
    }

    return availability.minDate; // fallback to original min date if no working day found
  }, [availability.minDate, availability.maxDate, excludeDate]);

  if (requestSessionMutation.isPending) {
    return <FancyLoader />;
  }

  const sessionTypesSelector = (
    <Select
      allowDeselect={false}
      value={selectedSessionType.id}
      onChange={(value) => {
        if (value) {
          onSessionTypeChange(value);
        }
      }}
      data={sessionTypes.map((type) => ({
        value: type.id,
        label: `${type.name} ${type.online ? " - Online" : ""} (${type.durationMinutes} min)`,
      }))}
    />
  );

  return (
    <Stack gap="md">
      <Header title="Request Session" />
      <Stack gap="md">
        <Text size="lg">Select Session Type</Text>
        {sessionTypesSelector}

        <Text size="lg">
          Select Available {instantBooking ? "Date" : "Dates"}
        </Text>
        <Center>
          <DatePicker
            size="md"
            defaultDate={firstAvailableDate}
            excludeDate={excludeDate}
            value={selectedDate}
            renderDay={renderDate}
            onChange={(value) => {
              if (typeof value === "string") {
                setSelectedDate(new Date(value));
              } else {
                setSelectedDate(value);
              }
            }}
            minDate={df.parseISO(availability.minDate)}
            maxDate={df.parseISO(availability.maxDate)}
            highlightToday
          />
        </Center>

        <Text size="md">Select Time {instantBooking ? "Slot" : "Slots"}</Text>
        <Center>
          <SimpleGrid cols={3} w="100%">
            {availableTimes.map((time) => (
              <Button
                w="100%"
                p="xs"
                key={time.toISOString()}
                variant={
                  selectedSlots.some((slot) => df.isEqual(slot, time))
                    ? "filled"
                    : "outline"
                }
                onClick={() => onSlotClick(time)}
              >
                {df.format(time, "HH:mm")}
              </Button>
            ))}
          </SimpleGrid>
        </Center>

        <Stack>
          <Text size="md">
            Selected Time {instantBooking ? "Slot" : "Slots"}
          </Text>
          {selectedSlots.map((slot) => (
            <Group key={slot.toISOString()} justify="space-between">
              <Text size="sm">{`${slot.toLocaleDateString(undefined, { year: "numeric", month: "long", day: "numeric" })} - ${df.format(slot, "HH:mm")}`}</Text>
              <Button
                color="red.8"
                size="xs"
                onClick={() => onRemoveSlot(slot)}
              >
                Remove
              </Button>
            </Group>
          ))}
        </Stack>

        <Button
          fullWidth
          onClick={handleSubmit}
          disabled={selectedSlots.length === 0 || !selectedSessionType}
          loading={requestSessionMutation.isPending}
        >
          {instantBooking ? "Book Session" : "Submit Proposed Dates"}
        </Button>
      </Stack>
    </Stack>
  );
};

export const RequestSession = (props: {
  params: {
    trainerId: string;
  };
}) => {
  const { trainerId } = props.params;
  const [searchParams] = useSearchParams();
  const urlSessionTypeId = searchParams.get("sessionTypeId");
  const [selectedSessionTypeId, setSelectedSessionTypeId] = useState<
    string | null
  >(urlSessionTypeId);

  const { data: sessionTypes } = client.useQuery(
    "get",
    "/owner/trainer/{trainerId}/session-types",
    {
      params: {
        path: {
          trainerId,
        },
      },
    },
  );

  // Set initial session type ID when session types are loaded (if not already set from URL)
  useEffect(() => {
    if (sessionTypes?.[0]?.id && !selectedSessionTypeId) {
      setSelectedSessionTypeId(sessionTypes[0].id);
    }
  }, [sessionTypes, selectedSessionTypeId]);

  // Use the selected session type ID (either from URL or user selection)
  const effectiveSessionTypeId = selectedSessionTypeId;

  const { data: availability } = client.useQuery(
    "get",
    "/owner/trainer/{trainerId}/availability",
    {
      params: {
        path: {
          trainerId,
        },
        query: {
          sessionTypeId: effectiveSessionTypeId || "",
        },
      },
    },
    {
      enabled: !!effectiveSessionTypeId,
    },
  );

  const { data: requestedSessions } = client.useQuery(
    "get",
    "/owner/sessions/",
    {
      params: {
        query: {
          statuses: ["REQUESTED"],
          trainerId,
        },
      },
    },
  );

  if (requestedSessions && requestedSessions.sessions.length > 0) {
    return (
      <Stack>
        <Header title="Request Session" />

        <Text>
          You already have a session requested with this trainer. You can only
          request one session per trainer at a time. Please wait for the trainer
          to respond to your request. You can message the trainer for further
          information. Or feel free to contact support for any questions.
        </Text>
        <LinkButton fullWidth href={UniversalRoutes.HOME}>
          Message Trainer
        </LinkButton>
        <LinkButton fullWidth href={UniversalRoutes.HOME} variant="outline">
          Message Support
        </LinkButton>
      </Stack>
    );
  }

  if (
    !availability ||
    !requestedSessions ||
    !sessionTypes ||
    !effectiveSessionTypeId
  ) {
    return <FancyLoader>Loading availability...</FancyLoader>;
  }

  if (sessionTypes.length === 0) {
    return <Text>Trainer does not have any session types available.</Text>;
  }

  return (
    <RequestSessionContent
      trainerId={trainerId}
      sessionTypes={
        sessionTypes as [
          TrainerDetailsSessionTypeOutput,
          ...TrainerDetailsSessionTypeOutput[],
        ]
      }
      availability={availability}
      selectedSessionTypeId={effectiveSessionTypeId}
      onSessionTypeChange={setSelectedSessionTypeId}
    />
  );
};
